import type { TextInputSuffix } from '@ksyos/design-system';
import { Box, Button, DropdownFilter, Modal, Radio, Spacer, TextInput } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IEmbloomQuestionnaire } from '../../../../interfaces/embloomQuestionnaire';
import type { ITableRow } from '../../../../userDataManagement/SelectableSortableTable';
import { AbortableFetch } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { SortDirection } from '../../../../../utils/sortDirection';
import { BadgeWithLabel } from '../../../../elements/BadgeWithLabel';
import { ListPager } from '../../../../elements/ListPager';
import { SelectableSortableTable } from '../../../../userDataManagement/SelectableSortableTable';

interface IAddEmbloomQuestionnaireModalProps {
    checkedQuestionnaires: { embloomQuestionnaire: IEmbloomQuestionnaire }[];
    onCloseModal: () => void;
    onSave: (questionnaires: IEmbloomQuestionnaire[]) => Promise<void>;
}

export function AddEmbloomQuestionnaireModal(props: IAddEmbloomQuestionnaireModalProps) {
    const abortableFetch = new AbortableFetch();
    const suffixProp: TextInputSuffix = {
        icon: 'search',
        labelText: 'aria labelled text',
        onSuffixClick: async () => await loadAddableEmbloomQuestionnaires(),
    };
    const [selectableEmbloomQuestionnaires, setSelectableEmbloomQuestionnaires] = useState<IEmbloomQuestionnaire[]>([]);
    const [checkedQuestionnairesInModal, setCheckedQuestionnairesInModal] = useState<IEmbloomQuestionnaire[]>([]);
    const [sortDirection, setSortDirection] = useState(SortDirection.asc);
    const [isSearching, setIsSearching] = useState(false);
    const [searchText, setSearchText] = useState<string>('');
    const [total, setTotal] = useState<number>(0);
    const [decisionSupportOrOtherFilter, setDecisionSupportOrOtherFilter] = useState<boolean>();

    const [page, setPage] = useState(1);
    const limit = 14;

    useEffect(() => {
        loadAddableEmbloomQuestionnaires();
    }, [sortDirection, searchText, page, decisionSupportOrOtherFilter]);

    return (
        <Modal
            isOpen
            onClose={props.onCloseModal}
            size="large"
        >
            <Modal.Header>{translateKey('embloomQuestionnaires.addEmbloomQuestionnaires')}</Modal.Header>
            <Modal.CloseButton />
            <Modal.Body>
                <Box
                    display="grid"
                    gap={2}
                    gridTemplateColumns={['1fr', '1fr max-content']}
                    mb={6}
                >
                    <TextInput
                        onChange={async (e) => {
                            setPage(1);
                            setSearchText(e.target.value);
                        }}
                        onEnterKeyPress={async () => await loadAddableEmbloomQuestionnaires()}
                        onReset={() => setSearchText('')}
                        placeholder={translateKey('embloomQuestionnaires.searchByName')}
                        suffix={suffixProp}
                        value={searchText}
                    />
                    <DropdownFilter
                        count={typeof decisionSupportOrOtherFilter === 'boolean' ? 1 : 0}
                        label="Filters"
                        noPadding
                        onReset={async () => await setDecisionSupportOrOtherFilter(undefined)}
                        onSubmit={async () => await loadAddableEmbloomQuestionnaires()}
                    >
                        <DropdownFilter.Accordion type="multiple">
                            <DropdownFilter.AccordionItem
                                label={'Type'}
                                value={'Type'}
                            >
                                <Spacer
                                    direction="vertical"
                                    spacing="small"
                                >
                                    <Radio
                                        checked={decisionSupportOrOtherFilter === true}
                                        label={translateKey('embloomQuestionnaires.decisionSupportQuestionnaires')}
                                        onChange={async (value) =>
                                            await setDecisionSupportOrOtherFilter(value ? true : undefined)
                                        }
                                        value="desicionSupport"
                                    />
                                    <Radio
                                        checked={decisionSupportOrOtherFilter === false}
                                        label={translateKey('embloomQuestionnaires.otherQuestionnaires')}
                                        onChange={async (value) =>
                                            await setDecisionSupportOrOtherFilter(value ? false : undefined)
                                        }
                                        value="other"
                                    />
                                </Spacer>
                            </DropdownFilter.AccordionItem>
                        </DropdownFilter.Accordion>
                    </DropdownFilter>
                </Box>
                <SelectableSortableTable
                    onSelect={async (ids) => await handleSelect(ids)}
                    onSort={(newSortKey: string, newSortDirection: SortDirection) => handleSort(newSortDirection)}
                    selectedRowKeys={checkedQuestionnairesInModal.map(
                        (checkedQuestionnaire) => checkedQuestionnaire.id
                    )}
                    sortDirection={sortDirection}
                    sortKey={'embloomQuestionnaire.name'}
                    tableHeaders={[
                        {
                            columnKey: 'embloomQuestionnaire.name',
                            children: translateKey('embloomQuestionnaires.nameDescription'),
                            isSortable: true,
                        },
                    ]}
                    tableRows={getTableRows()}
                />
                <ListPager
                    isDisabled={isSearching}
                    itemsPerPage={total < limit ? total : limit}
                    onChangePage={async (pageNum: number) => await setPage(pageNum)}
                    page={page}
                    showNoResultsText
                    total={total}
                />
            </Modal.Body>
            <Modal.Footer>
                {checkedQuestionnairesInModal.length > 0 && (
                    <BadgeWithLabel
                        color="neutral"
                        label={translateKey('common.isSelected')}
                        value={checkedQuestionnairesInModal.length.toString()}
                    />
                )}
                <Button onClick={async () => await props.onSave(checkedQuestionnairesInModal)}>
                    {translateKey('embloomQuestionnaires.saveQuestionnaires')}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    async function loadAddableEmbloomQuestionnaires() {
        setIsSearching(true);

        let url = `/api/embloomQuestionnaires?isActive=true&limit=${limit}&page=${page}&sortDirectionLabel=${sortDirection}`;

        const questionnaireIdsToExclude = props.checkedQuestionnaires
            .map((questionnaire) => `questionnaireIdsToExclude[]=${questionnaire.embloomQuestionnaire.id}`)
            .join('&');
        if (questionnaireIdsToExclude.length) {
            url += `&${questionnaireIdsToExclude}`;
        }
        if (searchText) {
            url += `&searchText=${searchText}`;
        }
        if (decisionSupportOrOtherFilter !== undefined) {
            if (decisionSupportOrOtherFilter) {
                url += '&decisionSupportEmbloomQuestionnairesOnly=true';
            }
            if (!decisionSupportOrOtherFilter) {
                url += '&nonDecisionSupportEmbloomQuestionnairesOnly=true';
            }
        }

        const result = await abortableFetch.fetchJson(url, 'GET');
        setSelectableEmbloomQuestionnaires(result.json.items);
        setTotal(result.json.total);
        setIsSearching(false);
    }

    function getTableRows() {
        return selectableEmbloomQuestionnaires.map((questionnaire) => {
            return {
                rowKey: questionnaire.id,
                cells: [
                    {
                        style: { fontWeight: 500 },
                        children: `${questionnaire.label} ${
                            questionnaire.description ? `- ${questionnaire.description}` : ''
                        }`,
                    },
                ],
            };
        }) as ITableRow[];
    }

    async function handleSelect(ids: string[]) {
        const checkedQuestionnaires = selectableEmbloomQuestionnaires.filter((questionnaire) =>
            ids.includes(questionnaire.id)
        );
        if (checkedQuestionnaires) {
            await setCheckedQuestionnairesInModal(checkedQuestionnaires);
        }
    }

    async function handleSort(newSortDirection: SortDirection) {
        setSortDirection(newSortDirection);
    }
}
